import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import { Phone } from 'lucide-react';
import { getSaveaPhoneTel } from './config/phoneConfig';
import axios from "axios";

// Import i18n configuration
import './i18n';

// Import components
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { AnnouncementBanner } from './components/AnnouncementBanner';
import { CookieBanner } from './components/CookieBanner';
import { StickyPhoneBar } from './components/StickyPhoneBar';
import { LanguageUrlSync } from './components/LanguageUrlSync';
import { ScrollToTop } from './components/ScrollToTop';
import { Button } from './components/ui/button';
import { Toaster } from "./components/ui/sonner";

// Import pages
import { HomePage } from './pages/HomePage';
import { ToolsPage } from './pages/ToolsPage';
import { EbooksPage } from './pages/EbooksPage';
import { EmailTemplatesPage } from './pages/EmailTemplatesPage';
import { ElectricityComparatorPage } from './pages/ElectricityComparatorPage';
import { InternetComparatorPage } from './pages/InternetComparatorPage';
import { MovingChecklistPage } from './pages/MovingChecklistPage';
import { ExpenseMonitorPage } from './pages/ExpenseMonitorPage';
import { SolarSimulatorPage } from './pages/SolarSimulatorPage';
import { PriceTrackerPage } from './pages/PriceTrackerPage';
import { InflationEvolutionPage } from './pages/InflationEvolutionPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { MethodologyPage } from './pages/MethodologyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { CookiePolicyPage } from './pages/CookiePolicyPage';
import { AntiInflationGuidePage } from './pages/AntiInflationGuidePage';
import { ProviderPage } from './pages/ProviderPage';
import { ProductsPage } from './pages/ProductsPage';
import { FAQPage } from './pages/FAQPage';
import { ComparateurGeneriquePage } from './pages/ComparateurGeneriquePage';
import { EDFPageV2 } from './pages/EDFPageV2';
import { EngiePageV2 } from './pages/EngiePageV2';

// Import styles
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const { t, i18n } = useTranslation();
  const [currentPath, setCurrentPath] = useState('');

  useEffect(() => {
    const updatePath = () => setCurrentPath(window.location.pathname);
    updatePath();
    window.addEventListener('popstate', updatePath);
    return () => window.removeEventListener('popstate', updatePath);
  }, []);

  const isProviderPage = currentPath.includes('/fournisseur/') || currentPath.includes('/fournisseurs/') || currentPath.includes('/proveedor/') || currentPath.includes('/proveedores/') || currentPath.includes('/comparer-fournisseurs');

  useEffect(() => {
    const testApi = async () => {
      try {
        const response = await axios.get(`${API}/`);
        console.log('API connected:', response.data.message);
      } catch (e) {
        console.error('API connection failed:', e);
      }
    };
    testApi();
  }, []);

  return (
    <HelmetProvider>
      <div className="App min-h-screen flex flex-col">
        <BrowserRouter>
          <ScrollToTop />
          <LanguageUrlSync />
        {!isProviderPage && <Header />}
        
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<Navigate to="/fr" replace />} />
            
            {/* French Routes */}
            <Route path="/fr" element={<HomePage />} />
            <Route path="/fr/outils" element={<ToolsPage />} />
            <Route path="/fr/ebooks" element={<EbooksPage />} />
            <Route path="/fr/modeles-emails" element={<EmailTemplatesPage />} />
            <Route path="/fr/demenagement" element={<MovingChecklistPage />} />
            <Route path="/fr/tracker-prix" element={<PriceTrackerPage />} />
            <Route path="/fr/simulateur-solaire" element={<SolarSimulatorPage />} />
            <Route path="/fr/suivi-depenses" element={<ExpenseMonitorPage />} />
            <Route path="/fr/inflation" element={<InflationEvolutionPage />} />
            <Route path="/fr/comparateur-internet" element={<InternetComparatorPage />} />
            <Route path="/fr/comparateur-electricite" element={<ElectricityComparatorPage />} />
            <Route path="/fr/a-propos" element={<AboutPage />} />
            <Route path="/fr/faq" element={<FAQPage />} />
            <Route path="/fr/contact" element={<ContactPage />} />
            <Route path="/fr/confidentialite" element={<PrivacyPolicyPage />} />
            <Route path="/fr/methodologie" element={<MethodologyPage />} />
            <Route path="/fr/guide-anti-inflation" element={<AntiInflationGuidePage />} />
            <Route path="/fr/produits-testes" element={<ProductsPage />} />
            
            {/* Pages fournisseurs France */}
            <Route path="/fr/fournisseur/offre-elec" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/offres-electricite" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/comparer-offre" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/comparer-energie" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/tarifs-electricite" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/contrat-elec" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/contrats-electricite" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/offre-energie" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/offres-energies" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/prix-elec" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/prix-electricite" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/tarif-energie" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/tarifs-energies" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/contrat-energie" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/contrats-energies" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/offre-electrique" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/offres-electriques" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/elecocite-jeety" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/fournisseur-geg" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/fournisseur-gaz-bordeaux" element={<ProviderPage />} />
            <Route path="/fr/comparer-fournisseurs" element={<ComparateurGeneriquePage />} />
            <Route path="/fr/fournisseur/elec-france" element={<EDFPageV2 />} />
            <Route path="/fr/fournisseur/comparateur" element={<EngiePageV2 />} />

            {/* Duplications FR NRJ : /fr/fournisseurs/{nom} */}
            <Route path="/fr/fournisseurs/engie" element={<ProviderPage />} />
            <Route path="/fr/fournisseurs/edf" element={<ProviderPage />} />
            <Route path="/fr/fournisseurs/ekwateur" element={<ProviderPage />} />
            <Route path="/fr/fournisseurs/totalenergies" element={<ProviderPage />} />
            <Route path="/fr/fournisseurs/vattenfall" element={<ProviderPage />} />
            <Route path="/fr/fournisseurs/mint-energie" element={<ProviderPage />} />
            <Route path="/fr/fournisseurs/ohm-energie" element={<ProviderPage />} />
            <Route path="/fr/fournisseurs/la-bellenergie" element={<ProviderPage />} />
            <Route path="/fr/fournisseurs/primeo-energie" element={<ProviderPage />} />
            <Route path="/fr/fournisseurs/alpiq" element={<ProviderPage />} />
            <Route path="/fr/fournisseurs/elmy" element={<ProviderPage />} />
            <Route path="/fr/fournisseurs/alterna-energie" element={<ProviderPage />} />
            <Route path="/fr/fournisseurs/leclerc-energies" element={<ProviderPage />} />
            <Route path="/fr/fournisseurs/grdf" element={<ProviderPage />} />
            <Route path="/fr/fournisseurs/ilek" element={<ProviderPage />} />
            <Route path="/fr/fournisseurs/enedis" element={<ProviderPage />} />
            <Route path="/fr/fournisseurs/eni" element={<ProviderPage />} />
            <Route path="/fr/fournisseurs/elecocite" element={<ProviderPage />} />
            <Route path="/fr/fournisseurs/geg" element={<ProviderPage />} />
            <Route path="/fr/fournisseurs/gaz-de-bordeaux" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/offre-internet" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/offres-internet" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/comparer-internet" element={<ProviderPage />} />
            <Route path="/fr/fournisseur/tarif-internet" element={<ProviderPage />} />
            
            {/* Spanish Routes */}
            <Route path="/es" element={<HomePage />} />
            <Route path="/es/herramientas" element={<ToolsPage />} />
            <Route path="/es/ebooks" element={<EbooksPage />} />
            <Route path="/es/modelos-email" element={<EmailTemplatesPage />} />
            <Route path="/es/mudanza" element={<MovingChecklistPage />} />
            <Route path="/es/seguimiento-precios" element={<PriceTrackerPage />} />
            <Route path="/es/simulador-solar" element={<SolarSimulatorPage />} />
            <Route path="/es/seguimiento-gastos" element={<ExpenseMonitorPage />} />
            <Route path="/es/inflacion" element={<InflationEvolutionPage />} />
            <Route path="/es/comparador-internet" element={<InternetComparatorPage />} />
            <Route path="/es/luz" element={<ElectricityComparatorPage />} />
            <Route path="/es/acerca-de" element={<AboutPage />} />
            <Route path="/es/faq" element={<FAQPage />} />
            <Route path="/es/contacto" element={<ContactPage />} />
            <Route path="/es/privacidad" element={<PrivacyPolicyPage />} />
            <Route path="/es/metodologia" element={<MethodologyPage />} />
            <Route path="/es/guia-anti-inflacion" element={
              <div className="p-8 text-center min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50">
                <div className="max-w-lg">
                  <div className="text-6xl mb-6">🚧</div>
                  <h1 className="text-3xl font-bold text-amber-800 mb-4">
                    Guía Anti-Inflación 2026
                  </h1>
                  <p className="text-amber-700 mb-6">
                    Estamos preparando la versión en español de nuestra guía completa para ahorrar dinero. 
                    ¡Muy pronto disponible con consejos adaptados al mercado español!
                  </p>
                  <div className="text-sm text-amber-600 mb-6">
                    Mientras tanto, nuestros especialistas están disponibles para ayudarte:
                  </div>
                  <Button 
                    onClick={() => window.open(`tel:${getSaveaPhoneTel('ES', 'electricity', 'LP')}`)}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Hablar con Especialista SAVEA
                  </Button>
                </div>
              </div>
            } />

            {/* Pages fournisseurs Espagne */}
            <Route path="/es/proveedor/oferta-luz" element={<ProviderPage />} />
            <Route path="/es/proveedor/ofertas-luz" element={<ProviderPage />} />
            <Route path="/es/proveedor/comparar-luz" element={<ProviderPage />} />
            <Route path="/es/proveedor/comprar-energia" element={<ProviderPage />} />
            <Route path="/es/proveedor/tarifa-luz" element={<ProviderPage />} />
            <Route path="/es/proveedor/tarifas-luz" element={<ProviderPage />} />
            <Route path="/es/proveedor/contrato-luz" element={<ProviderPage />} />
            <Route path="/es/proveedor/contratos-luz" element={<ProviderPage />} />
            <Route path="/es/proveedor/precio-luz" element={<ProviderPage />} />
            <Route path="/es/proveedor/precios-luz" element={<ProviderPage />} />
            <Route path="/es/proveedor/oferta-energia" element={<ProviderPage />} />
            <Route path="/es/proveedor/ofertas-energia" element={<ProviderPage />} />
            <Route path="/es/proveedor/comprar-luz" element={<ProviderPage />} />
            <Route path="/es/proveedor/oferta-internet" element={<ProviderPage />} />
            <Route path="/es/proveedor/ofertas-internet" element={<ProviderPage />} />
            <Route path="/es/proveedor/comprar-internet" element={<ProviderPage />} />
            <Route path="/es/proveedor/comparar-internet" element={<ProviderPage />} />
            <Route path="/es/proveedor/tarifa-internet" element={<ProviderPage />} />
            <Route path="/es/proveedor/tarifas-internet" element={<ProviderPage />} />
            <Route path="/es/proveedor/contrato-internet" element={<ProviderPage />} />

            {/* Duplications ES NRJ : /es/proveedores/{nom} */}
            <Route path="/es/proveedores/curenergia" element={<ProviderPage />} />
            <Route path="/es/proveedores/endesa" element={<ProviderPage />} />
            <Route path="/es/proveedores/energia-xxi" element={<ProviderPage />} />
            <Route path="/es/proveedores/plenitude" element={<ProviderPage />} />
            <Route path="/es/proveedores/factor-energia" element={<ProviderPage />} />
            <Route path="/es/proveedores/gana-energia" element={<ProviderPage />} />
            <Route path="/es/proveedores/iberdrola" element={<ProviderPage />} />
            <Route path="/es/proveedores/holaluz" element={<ProviderPage />} />
            <Route path="/es/proveedores/naturgy" element={<ProviderPage />} />
            <Route path="/es/proveedores/repsol" element={<ProviderPage />} />
            <Route path="/es/proveedores/baser" element={<ProviderPage />} />
            <Route path="/es/proveedores/octopus-energy" element={<ProviderPage />} />
            <Route path="/es/proveedores/total-energies-es" element={<ProviderPage />} />

            {/* Duplications ES BOX : /es/proveedores/{nom} */}
            <Route path="/es/proveedores/digi-mobil" element={<ProviderPage />} />
            <Route path="/es/proveedores/lowi" element={<ProviderPage />} />
            <Route path="/es/proveedores/movistar" element={<ProviderPage />} />
            <Route path="/es/proveedores/o2" element={<ProviderPage />} />
            <Route path="/es/proveedores/simyo" element={<ProviderPage />} />
            <Route path="/es/proveedores/vodafone" element={<ProviderPage />} />
            <Route path="/es/proveedores/masmovil" element={<ProviderPage />} />

            {/* Legacy route redirects */}
            <Route path="/tools" element={<Navigate to="/fr/outils" replace />} />
            <Route path="/ebooks" element={<Navigate to="/fr/ebooks" replace />} />
            <Route path="/templates" element={<Navigate to="/fr/modeles-emails" replace />} />
            <Route path="/electricity" element={<Navigate to="/fr/comparateur-electricite" replace />} />
            <Route path="/internet" element={<Navigate to="/fr/comparateur-internet" replace />} />
            <Route path="/moving" element={<Navigate to="/fr/demenagement" replace />} />
            <Route path="/expenses" element={<Navigate to="/fr/suivi-depenses" replace />} />
            <Route path="/solar" element={<Navigate to="/fr/simulateur-solaire" replace />} />
            <Route path="/tracker" element={<Navigate to="/fr/tracker-prix" replace />} />
            <Route path="/inflation" element={<Navigate to="/fr/inflation" replace />} />
            <Route path="/about" element={<Navigate to="/fr/a-propos" replace />} />
            <Route path="/contact" element={<Navigate to="/fr/contact" replace />} />
            <Route path="/privacy" element={<Navigate to="/fr/confidentialite" replace />} />
            
            {/* Placeholder legal pages */}
            <Route path="/fr/conditions" element={<TermsOfServicePage />} />
            <Route path="/fr/cookies" element={<CookiePolicyPage />} />
            <Route path="/es/terminos" element={<TermsOfServicePage />} />
            <Route path="/es/cookies" element={<CookiePolicyPage />} />
            
            {/* 404 */}
            <Route path="*" element={
              <div className="p-8 text-center min-h-screen flex items-center justify-center">
                <div>
                  <h1 className="text-2xl font-bold mb-4">
                    {i18n.language === 'es' ? 'Página no encontrada' : 'Page non trouvée'}
                  </h1>
                  <p className="text-gray-600 mb-4">
                    {i18n.language === 'es' ? 'La página que buscas no existe.' : 'La page que vous cherchez n\'existe pas.'}
                  </p>
                  <button 
                    onClick={() => window.location.href = i18n.language === 'es' ? '/es' : '/fr'}
                    className="bg-sage-600 text-white px-6 py-2 rounded-full hover:bg-sage-700 transition-colors"
                  >
                    {i18n.language === 'es' ? 'Ir al inicio' : 'Aller à l\'accueil'}
                  </button>
                </div>
              </div>
            } />
          </Routes>
        </main>

        <Footer />
        <CookieBanner />
        <StickyPhoneBar hideOnV2={true} />
        <Toaster />
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

export default App;